import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import type { IPropsStore, IStructureAPI } from '@wix/thunderbolt-symbols'
import { PageFeatureConfigSymbol, FeatureStateSymbol, Props, StructureAPI } from '@wix/thunderbolt-symbols'
import type { QuickActionBarProps } from '@wix/thunderbolt-components'
import { color as colorUtils } from '@wix/thunderbolt-commons'
import type { ITPAEventsListenerManager } from 'feature-tpa'
import { TpaEventsListenerManagerSymbol } from 'feature-tpa'
import type { IFeatureState } from 'thunderbolt-feature-state'

import type {
	QuickActionBarPageConfig,
	QuickActionBarState,
	IDynamicActionsApi,
	DynamicActionsConfig,
	DynamicAction,
} from './types'
import { name, DynamicActionsConfigSymbol } from './symbols'

type DynamicActionItemProps = Required<QuickActionBarProps>['dynamicActions'][0]
type DynamicActionsState = QuickActionBarState['dynamicActions']

const dynamicActionsApiFactory = (
	pageConfig: QuickActionBarPageConfig,
	featureState: IFeatureState<QuickActionBarState>,
	propsStore: IPropsStore,
	dynamicActionsConfig: DynamicActionsConfig,
	structureAPI: IStructureAPI,
	tpaEventsListenerManager?: ITPAEventsListenerManager
): IDynamicActionsApi => {
	let localDynamicActions: DynamicActionsState = []
	const actionTriggeredCallbacks: { [appDefinitionId: string]: () => void } = {}

	const triggerQuickActionEvent = (appDefinitionId: string) => {
		if (tpaEventsListenerManager) {
			tpaEventsListenerManager.dispatch('QUICK_ACTION_TRIGGERED', () => ({ quickAction: appDefinitionId }), {
				appDefinitionId,
			})
		}
		if (actionTriggeredCallbacks[appDefinitionId]) {
			actionTriggeredCallbacks[appDefinitionId]()
		}
	}

	const updateQABComponentProps = () => {
		const state = featureState.get()
		const globalDynamicActions = state?.dynamicActions ?? []
		const structuredItems = propsStore.get('QUICK_ACTION_BAR')?.items ?? []

		const visibleDynamicActions = globalDynamicActions
			.concat(localDynamicActions)
			.filter(({ isVisible = true }) => isVisible)

		const withCustomStyle = visibleDynamicActions.length === 1 && structuredItems.length === 0

		const dynamicActions = visibleDynamicActions.map<DynamicActionItemProps>(
			({ appDefinitionId, hasNotifications = false, color, iconSvgContent }) => {
				const dynamicActionConfig = dynamicActionsConfig[appDefinitionId]
				const { text, itemType, color: defaultColor, iconSvgContent: defaultIconSvgContent } = dynamicActionConfig
				const colorAsRgbValues =
					(withCustomStyle && color && colorUtils.getSplitRgbValuesString(color)) ||
					colorUtils.getSplitRgbValuesString(defaultColor)!

				const svgString = withCustomStyle && iconSvgContent ? iconSvgContent : defaultIconSvgContent

				return {
					appDefinitionId,
					text,
					itemType,
					hasNotifications,
					color: colorAsRgbValues,
					svgString,
					onActivate() {
						triggerQuickActionEvent(appDefinitionId)
					},
				}
			}
		)

		propsStore.update({
			[pageConfig.componentId]: {
				dynamicActions,
			},
		})
	}

	const getUpdatedDynamicActionsState = (action: DynamicAction, dynamicActions: Readonly<DynamicActionsState>) => {
		const existingActionIndex = dynamicActions.findIndex((da) => da.appDefinitionId === action.appDefinitionId)
		const existingAction = existingActionIndex !== -1 ? dynamicActions[existingActionIndex] : undefined
		const updatedAction = { ...existingAction, ...action }
		const updatedDynamicActions = dynamicActions.slice()

		if (existingAction) {
			updatedDynamicActions[existingActionIndex] = updatedAction
		} else {
			updatedDynamicActions.push(updatedAction)
		}

		return updatedDynamicActions
	}

	return {
		updateAction(action, compId) {
			const { appDefinitionId } = action

			if (!(appDefinitionId in dynamicActionsConfig)) {
				throw new Error(`Dynamic actions are not supported for app definition id: "${appDefinitionId}"`)
			}
			const pageId = structureAPI.getCompPageId(compId)
			if (pageId === 'masterPage') {
				featureState.update((state?: Partial<QuickActionBarState>) => {
					const dynamicActions = getUpdatedDynamicActionsState(action, state?.dynamicActions ?? [])
					return {
						...state,
						dynamicActions,
					}
				})
			} else {
				localDynamicActions = getUpdatedDynamicActionsState(action, localDynamicActions)
			}

			updateQABComponentProps()
		},
		updateQABComponentProps,
		registerToDynamicActionActivated(appDefinitionId: string, actionTriggeredCallback: () => void) {
			actionTriggeredCallbacks[appDefinitionId] = actionTriggeredCallback
		},
	}
}

export const DynamicActionsApi = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(FeatureStateSymbol, name),
		Props,
		DynamicActionsConfigSymbol,
		StructureAPI,
		optional(TpaEventsListenerManagerSymbol),
	],
	dynamicActionsApiFactory
)
